import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { reportMetric } from '../../api/metrics';
import { MetricEvent } from './StreamFrame';
import { addQueryParams } from '../../utility/addQueryParams';

export function Dungeon(props: { streamKey: string; userID: string; iframeStyle: CSSProperties; onLoaded: () => void }) {
	const [loaded, setLoaded] = useState(false);
	const [retry, setRetry] = useState(0);

	// ensure that when the overlay is reloaded, the dungeon is reloaded too
	const cacheBust = useMemo(() => Math.round(1_000_000_000 * Math.random()), []);

	const iframeOrigin = /local-dungeon/.test(window.location.search)
		? 'http://localhost:8016'
		: /staging/.test(window.location.hostname)
			? 'https://staging.tangia-dungeon.pages.dev'
			: 'https://dungeon.cf.tangia.co';

	const iframeURL = addQueryParams(iframeOrigin, {
		key: props.streamKey,
		userID: props.userID,
		backendAPI: import.meta.env.VITE_BACKEND_API,
		cacheBust,
		retry,
	});

	// retry loading frame after loading timeout
	const loadingTimeoutMs = 30_000;
	useEffect(() => {
		if (loaded) {
			return;
		}
		const timeout = setTimeout(() => {
			setRetry((old) => {
				console.log('failed to load dungeon iframe within timeout. Retrying...', loadingTimeoutMs);
				reportMetric({
					event_key: props.streamKey,
					created_ms: new Date().getTime(),
					url: iframeOrigin,
					metric: 'dungeon_loading_error',
					value: old,
					data: {
						error: 'timeout after ' + loadingTimeoutMs,
						attempt: old,
					},
				});
				return old + 1;
			});
		}, loadingTimeoutMs);
		return () => {
			clearTimeout(timeout);
		};
	}, [iframeURL, loaded]);

	useEffect(() => {
		const handelIFrameMessage = (event: MessageEvent) => {
			if (event.origin !== iframeOrigin) {
				return;
			}
			switch (event.data.type) {
				case 'dungeon':
					console.log('dungeon loaded');
					setLoaded(true);
					props.onLoaded();
					break;

				case 'error':
					console.log('Received error from dungeon', event.data);
					reportMetric({
						event_key: props.streamKey,
						created_ms: new Date().getTime(),
						url: iframeOrigin,
						metric: 'dungeon_error',
						value: 1,
						data: {
							error: event.data,
						},
					});
					break;

				case 'metric':
					const metricEvent = event.data as MetricEvent;
					reportMetric({
						event_key: props.streamKey,
						created_ms: new Date().getTime(),
						url: iframeOrigin,
						metric: 'dungeon_' + metricEvent.metric,
						value: metricEvent.value,
						data: {},
					});
					break;

				default:
					console.warn('Unknown message from dungeon:', event.data);
					break;
			}
		};
		window.addEventListener('message', handelIFrameMessage);
		return () => {
			window.removeEventListener('message', handelIFrameMessage);
		};
	}, []);

	return (
		<iframe
			src={iframeURL}
			allow="autoplay *; encrypted-media *;"
			scrolling="no"
			className={loaded ? 'absolute bg-transparent' : 'hidden'}
			style={props.iframeStyle}
			// maybe this will never fire. MDN says it doesn't to block local network probing, but it's unclear if that's also true
			// if the target is not on the local network
			onError={(e) => {
				reportMetric({
					event_key: props.streamKey,
					created_ms: new Date().getTime(),
					url: iframeOrigin,
					metric: 'dungeon_loading_error',
					value: 1,
					data: {
						error: `loading error: ${e}`,
						attempt: retry,
					},
				});
			}}
		/>
	);
}
